/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { LeaseService } from '../services/lease-service';
import { leasesKeys } from './leases-keys';
import type { LeaseDocumentCategory, LeaseSettings } from '../types/lease.types';

export interface LeaseSettingsViewModel {
  isLoading: boolean;
  isError: boolean;
  settings: LeaseSettings | null;
  /** Null when the agency never chose one — the platform default applies. */
  defaultPaymentDay: number | null;
  businessDayRule: string | null;
  shiftDiscountDeadline: boolean | null;
  documentCategories: LeaseDocumentCategory[];
  saveDueDateDefaults: (patch: {
    default_payment_day?: number | null;
    business_day_rule?: string | null;
    shift_discount_deadline?: boolean | null;
  }) => Promise<void>;
  addDocumentCategory: (label: string) => Promise<void>;
  renameDocumentCategory: (categoryId: string, label: string) => Promise<void>;
}

/**
 * The agency's lease settings — how a new contract's due date is decided,
 * and what its documents are filed under.
 *
 * One query for both: the backend keeps them in a single document, so the
 * screen reads them in one call and every write invalidates the same key.
 *
 * @param agencyId - The owning agency.
 * @returns The view-model: the settings, load state, and the three writes.
 */
export function useLeaseSettings(agencyId: string): LeaseSettingsViewModel {
  const queryClient = useQueryClient();
  const enabled = Boolean(agencyId);

  const settingsQuery = useQuery({
    queryKey: leasesKeys.settings(agencyId),
    queryFn: () => LeaseService.readSettings(agencyId),
    enabled,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: leasesKeys.settings(agencyId) });

  const policyMutation = useMutation({
    mutationFn: (patch: Parameters<typeof LeaseService.updateSettings>[1]) =>
      LeaseService.updateSettings(agencyId, patch),
    onSuccess: invalidate,
  });

  const addMutation = useMutation({
    mutationFn: (label: string) => LeaseService.addDocumentCategory(agencyId, { label }),
    onSuccess: invalidate,
  });

  const renameMutation = useMutation({
    mutationFn: (args: { categoryId: string; label: string }) =>
      LeaseService.updateDocumentCategory(agencyId, args.categoryId, { label: args.label }),
    onSuccess: invalidate,
  });

  const settings = settingsQuery.data ?? null;

  return {
    isLoading: settingsQuery.isLoading,
    isError: settingsQuery.isError,
    settings,
    defaultPaymentDay: settings?.default_payment_day ?? null,
    businessDayRule: settings?.business_day_rule ?? null,
    shiftDiscountDeadline: settings?.shift_discount_deadline ?? null,
    documentCategories: settings?.document_categories ?? [],
    saveDueDateDefaults: async (patch) => {
      await policyMutation.mutateAsync(patch);
    },
    addDocumentCategory: async (label) => {
      await addMutation.mutateAsync(label);
    },
    renameDocumentCategory: async (categoryId, label) => {
      await renameMutation.mutateAsync({ categoryId, label });
    },
  };
}
